import {Component, DoCheck, Input, ViewChild, ViewChildren, QueryList, ElementRef, OnChanges, OnDestroy,
  SimpleChanges, AfterViewInit, AfterViewChecked, HostListener} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {TranslateService} from '@ngx-translate/core';
import {Subscription} from 'rxjs/Subscription';
import {Observable} from 'rxjs/Observable';
import 'rxjs/add/observable/timer';
import {GradeGrowthReadContext} from './grade-growth-read-context';
import {refreshGrowthSource, growthImportErrorKey, GrowthImportResponse, GrowthImportError,
  GROWTH_IMPORT_STATUS, GROWTH_IMPORT_FEEDBACK} from './grade-growth-source-refresh';
import {dealPayrollGradeKey} from 'app/models/deal-payroll-presentation';
import {GradePromotionEvent, GrowthDashboardData, GrowthLearningItem, GrowthLearningSummary} from './grade-growth.model';
import {growthDecimalText, growthEvaluationView, GrowthConditionView, GrowthLearningPreviewView, trackGrowthLearningCount} from './grade-growth-presentation';
import {GrowthMilestone, GrowthChartLayout, growthMilestones, growthChartLayout,
  growthFinitePoints, growthCompatibleMoney, growthAvailableReference, growthMoneyStatus} from './grade-growth-chart';

const GRADE_GROWTH_ERROR = 'GRADE_GROWTH_ERROR';
const INITIAL_CHART_WIDTH = 300;
const EMPTY = '';
const LEARNING_PAGE_SIZE = 10;
const ZERO = 0;
const NO_NEXT_GRADE = 'NO_NEXT_GRADE';
const COMMITTED = 'COMMITTED';
const APPLIED = 'APPLIED';
const APPLYING = 'APPLYING';
const POLICY_GRADE_MISMATCH = 'POLICY_GRADE_MISMATCH';
const GROWTH_LEARNING = 'growth-learning-';
const RESIZE_DEBOUNCE_MS = 80;
const FULLSCREEN_CHANGE = 'fullscreenchange';
const WEBKIT_FULLSCREEN_CHANGE = 'webkitfullscreenchange';
const GROWTH_PATH = '/grade-growth';
const MAX_MONTHS = 12;
const DEFAULT_LOCALE = 'ru';
const STALE = 'STALE';
const VALID = 'VALID';
const GRADE_GROWTH_TOTAL_BALANCE = 'GRADE_GROWTH_TOTAL_BALANCE';
const FORBIDDEN = 403;
const UNAUTHORIZED = 401;
const GRADE_GROWTH_REFRESH_ERROR = 'GRADE_GROWTH_REFRESH_ERROR';
const GRADE_GROWTH_DENIED = 'GRADE_GROWTH_DENIED';
const GRADE_GROWTH_SESSION_EXPIRED = 'GRADE_GROWTH_SESSION_EXPIRED';
const LEARNING_PAGE_PATH = '/learning?cursor=';
const LEARNING_PAGE_ERROR = 'GRADE_GROWTH_LEARNING_PAGE_ERROR';
const LEARNING_CONTEXT_CHANGED = 'GRADE_GROWTH_LEARNING_CONTEXT_CHANGED';
const CONDITION_UNKNOWN = 'GRADE_GROWTH_CONDITION_UNKNOWN';
const CONDITION_NOT_APPLICABLE = 'GRADE_GROWTH_CONDITION_NOT_APPLICABLE';
const NOT_APPLICABLE = 'NOT_APPLICABLE';

interface LearningPageRequest {
  userId: string;
  contextVersion: string;
  cursor: string;
  generation: number;
  sequence: number;
}

interface FullscreenTarget {
  ownerDocument: FullscreenDocument;
  requestFullscreen?: () => Promise<void> | void;
  webkitRequestFullscreen?: () => Promise<void> | void;
  msRequestFullscreen?: () => Promise<void> | void;
}
interface FullscreenDocument {
  activeElement: Element;
  fullscreenElement?: Element;
  webkitFullscreenElement?: Element;
  addEventListener(type: string, listener: EventListenerOrEventListenerObject): void;
  removeEventListener(type: string, listener: EventListenerOrEventListenerObject): void;
  exitFullscreen?: () => Promise<void> | void;
  webkitExitFullscreen?: () => Promise<void> | void;
  msExitFullscreen?: () => Promise<void> | void;
}

@Component({selector: 'app-grade-growth', templateUrl: './grade-growth.component.html', styleUrls: ['./grade-growth.component.css'],
  providers: [GradeGrowthReadContext]})
export class GradeGrowthComponent implements OnChanges, DoCheck, OnDestroy, AfterViewInit, AfterViewChecked {
  @Input() userId: string;
  @Input() visible: boolean;
  @Input() selfProfile = false;
  @Input() promotionAction = false;
  promotionRefreshToken = ZERO;
  @ViewChild('growthRoot') root: ElementRef;
  @ViewChild('chartRoot') chartRoot: ElementRef;
  @ViewChild('learningToggle') learningToggle: ElementRef;
  @ViewChild('refreshButton') refreshButton: ElementRef;
  @ViewChild('readRetry') readRetry: ElementRef;
  @ViewChild('learningMore') learningMore: ElementRef;
  @ViewChild('learningRetry') learningRetry: ElementRef;
  @ViewChildren('milestoneLabel') milestoneLabels: QueryList<ElementRef>;
  expanded = false;
  loading = false;
  error = false;
  errorKey = GRADE_GROWTH_ERROR;
  sourceFeedbackKey = EMPTY;
  sourceError = false;
  canRetryRead = true;
  data: GrowthDashboardData = null;
  milestones: GrowthMilestone[] = [];
  layout: GrowthChartLayout = growthChartLayout([], null, {width: INITIAL_CHART_WIDTH});
  gradeKey = EMPTY;
  nextGradeKey = EMPTY;
  conditionTargetKey = EMPTY;
  conditionGradeKey = EMPTY;
  totalCondition: GrowthConditionView = null;
  levelCondition: GrowthConditionView = null;
  learningCondition: GrowthLearningPreviewView = null;
  balanceText = EMPTY;
  balance: number = null;
  balanceKindKey = EMPTY;
  remainingText = EMPTY;
  moneyStatusKey = EMPTY;
  moneyMonth = EMPTY;
  hasMoney = false;
  hasReference = false;
  referenceMonth = EMPTY;
  stale = false;
  observedAt = EMPTY;
  learning: GrowthLearningSummary = null;
  learningItems: GrowthLearningItem[] = [];
  learningExpanded = false;
  learningLimit = LEARNING_PAGE_SIZE;
  learningLoading = false;
  learningErrorKey = EMPTY;
  learningNeedsRefresh = false;
  fullscreen = false;
  fullscreenSupported = false;
  private request: Subscription;
  private sourceRequestId: string;
  private learningRequest: Subscription;
  private learningSequence = ZERO;
  private languageChange: Subscription;
  private generation = ZERO;
  private refreshPending = false;
  private layoutDirty = true;
  private layoutTimer: Subscription;
  private resizeTimer: Subscription;
  private nativeDocument: FullscreenDocument;
  private restoreFocus: HTMLElement;
  private readActionFocus: HTMLElement;
  private learningActionFocus: HTMLElement;
  private owner: string;

  constructor(private http: HttpClient, private context: GradeGrowthReadContext, private translate: TranslateService) {
    this.owner = context.owner();
    this.languageChange = translate.onLangChange.subscribe(() => this.refreshPresentation());
  }

  ngDoCheck(): void {
    const owner = this.context.owner();
    if (this.owner === owner) { return; }
    this.owner = owner; this.exitFullscreen(); this.disposeRequest(); this.clearData();
    this.loading = false; this.error = false; this.canRetryRead = true; this.refreshPending = true;
    if (this.expanded && owner) { this.load(); }
  }

  get noNextGrade(): boolean {
    return !!this.data && !!this.data.evaluation && this.data.evaluation.state === NO_NEXT_GRADE;
  }
  get applicationMonth(): string {
    const context = this.data && this.data.calculationContext;
    return context && (context.status === COMMITTED || context.status === APPLIED) ? context.effectiveMonth : EMPTY;
  }
  get applying(): boolean {
    return !!this.data && !!this.data.calculationContext && this.data.calculationContext.status === APPLYING;
  }
  get policyMismatch(): boolean {
    return !!this.data && !!this.data.calculationContext && this.data.calculationContext.status === POLICY_GRADE_MISMATCH;
  }
  get learningHasMore(): boolean {
    return !!this.learning && (this.learning.items.length > this.learningLimit || !!this.learning.nextCursor);
  }
  get learningListId(): string { return GROWTH_LEARNING + this.userId; }

  ngOnChanges(changes?: SimpleChanges): void {
    if (!changes || changes.userId) {
      this.exitFullscreen(); this.disposeRequest(); this.clearData();
      this.expanded = false; this.loading = false; this.error = false;
      this.refreshPending = false; this.canRetryRead = true;
    }
    if (changes && changes.visible && this.visible === false) { this.exitFullscreen(); }
    if (this.visible != null && this.visible !== this.expanded) { this.toggle(); }
  }
  ngAfterViewInit(): void {
    const target = <FullscreenTarget>(this.root && this.root.nativeElement);
    this.fullscreenSupported = !!target && !!(target.requestFullscreen || target.webkitRequestFullscreen || target.msRequestFullscreen);
  }
  ngAfterViewChecked(): void {
    this.restoreActionFocus();
    if (!this.layoutDirty || this.layoutTimer || !this.expanded || !this.chartRoot) { return; }
    this.layoutDirty = false;
    this.layoutTimer = Observable.timer(ZERO).subscribe(() => {
      this.layoutTimer = null;
      const width = this.chartRoot && this.chartRoot.nativeElement.clientWidth;
      if (!width) { return; }
      const sized = growthChartLayout(this.milestones, this.balance, {width: width});
      const widthChanged = this.layout.labelWidth !== sized.labelWidth;
      const heights = this.milestoneLabels ? this.milestoneLabels.toArray().map(label => label.nativeElement.offsetHeight) : [];
      this.layout = growthChartLayout(this.milestones, this.balance, {width: width, heights: heights});
      this.layoutDirty = widthChanged;
    });
  }
  @HostListener('window:resize') onResize(): void {
    if (this.resizeTimer) { this.resizeTimer.unsubscribe(); }
    this.resizeTimer = Observable.timer(RESIZE_DEBOUNCE_MS).subscribe(() => this.layoutDirty = true);
  }
  ngOnDestroy(): void {
    this.languageChange.unsubscribe(); this.exitFullscreen(); this.disposeRequest();
    if (this.layoutTimer) { this.layoutTimer.unsubscribe(); }
    if (this.resizeTimer) { this.resizeTimer.unsubscribe(); }
    if (this.nativeDocument) {
      this.nativeDocument.removeEventListener(FULLSCREEN_CHANGE, this.onFullscreenChange);
      this.nativeDocument.removeEventListener(WEBKIT_FULLSCREEN_CHANGE, this.onFullscreenChange);
    }
  }
  toggle(): void {
    this.expanded = !this.expanded; this.layoutDirty = true;
    if (this.expanded && (!this.data || this.refreshPending) && !this.loading) { this.load(); }
  }
  refresh(): void {
    if (this.loading) { this.disposeRequest(); this.loading = false; }
    this.refreshPending = true;
    if (this.expanded) { this.load(); }
  }
  refreshSource(): void {
    if (this.loading || !this.userId || !this.canRetryRead) { return; }
    if (!this.context.canRefreshSource()) { this.refresh(); return; }
    this.disposeRequest();
    const userId = this.userId; const generation = this.generation;
    this.loading = true; this.error = false; this.sourceError = false;
    this.sourceFeedbackKey = GROWTH_IMPORT_FEEDBACK.loading;
    try {
      this.sourceRequestId = this.sourceRequestId || this.context.sourceRequestId();
      this.request = refreshGrowthSource(this.http, this.context.sourceRefreshUrl(userId), this.sourceRequestId)
        .subscribe(run => this.sourceRefreshed(userId, generation, run),
          error => this.sourceFailed(userId, generation, error));
    } catch (error) { this.sourceFailed(userId, generation, error); }
  }
  private sourceRefreshed(userId: string, generation: number, run: GrowthImportResponse): void {
    if (!this.currentRequest(userId, generation)) { return; }
    this.loading = false; this.sourceRequestId = null;
    this.sourceError = run.status === GROWTH_IMPORT_STATUS.superseded;
    this.sourceFeedbackKey = this.sourceError ? GROWTH_IMPORT_FEEDBACK.superseded
      : run.status === GROWTH_IMPORT_STATUS.partial ? GROWTH_IMPORT_FEEDBACK.partial : EMPTY;
    if (!this.sourceError) { this.load(); }
  }
  private sourceFailed(userId: string, generation: number, failure: GrowthImportError): void {
    if (!this.currentRequest(userId, generation)) { return; }
    if (failure && failure.status === UNAUTHORIZED) { this.fail(userId, generation, failure.status); return; }
    this.loading = false; this.sourceError = true; this.sourceFeedbackKey = growthImportErrorKey(failure);
    this.load();
  }
  promotionChanged(event: GradePromotionEvent): void {
    if (!event || event.employeeId !== this.userId) { return; }
    this.refresh();
  }
  load(): void {
    if (!this.userId || this.loading || !this.canRetryRead) { return; }
    this.disposeRequest();
    this.readActionFocus = this.focusedControl(this.readRetry);
    const userId = this.userId;
    const generation = this.generation;
    this.loading = true; this.error = false;
    this.request = this.http.get<GrowthDashboardData>(this.context.userUrl(userId) + GROWTH_PATH)
      .subscribe(value => this.apply(userId, generation, value), error => this.fail(userId, generation, error && error.status));
  }
  private apply(userId: string, generation: number, value: GrowthDashboardData): void {
    if (!this.currentRequest(userId, generation)) { return; }
    if (!value || value.userId !== userId || !Array.isArray(value.months)
        || value.months.length > MAX_MONTHS || !Array.isArray(value.grades)) { this.fail(userId, generation); return; }
    this.data = value; this.loading = false; this.refreshPending = false; this.error = false;
    this.promotionRefreshToken++;
    this.disposeLearningRequest(); this.learningLimit = LEARNING_PAGE_SIZE;
    this.learningErrorKey = EMPTY; this.learningNeedsRefresh = false;
    this.refreshPresentation();
  }
  private refreshPresentation(): void {
    if (!this.data) { return; }
    this.applyBalance(); this.applyConditions(); this.applyLearning();
    this.milestones = growthMilestones(this.data, this.translate.currentLang || DEFAULT_LOCALE);
    const width = this.chartRoot && this.chartRoot.nativeElement.clientWidth || this.layout.width;
    this.layout = growthChartLayout(this.milestones, this.balance, {width: width});
    this.moneyStatusKey = growthMoneyStatus(this.data);
    const money = growthCompatibleMoney(this.data);
    this.hasMoney = this.milestones.some(step => !!step.moneyText);
    this.moneyMonth = this.hasMoney && money ? money.month : EMPTY;
    const reference = growthAvailableReference(this.data);
    this.hasReference = this.milestones.some(step => !!step.referenceText);
    this.referenceMonth = this.hasReference && reference ? reference.rateMonth || EMPTY : EMPTY;
    this.updateLearningItems(); this.layoutDirty = true;
  }
  private applyBalance(): void {
    const evaluation = this.data.evaluation;
    const total = evaluation && evaluation.totalPoints;
    this.stale = !!total && total.status === STALE;
    const knownTotal = !!total && (total.status === VALID || this.stale) && growthFinitePoints(total.value);
    this.balance = knownTotal ? Number(total.value) : null;
    this.balanceText = knownTotal ? growthDecimalText(total.value) : EMPTY;
    this.balanceKindKey = GRADE_GROWTH_TOTAL_BALANCE;
    const grade = this.data.gradeCode || evaluation && evaluation.growthGrade && evaluation.growthGrade.code;
    this.gradeKey = grade ? dealPayrollGradeKey(grade) : EMPTY;
    this.nextGradeKey = grade && evaluation && evaluation.target ? dealPayrollGradeKey(evaluation.target.code) : EMPTY;
    this.remainingText = this.nextGradeKey && growthFinitePoints(evaluation.remainingPoints)
      ? growthDecimalText(evaluation.remainingPoints) : EMPTY;
    const source = evaluation && evaluation.planfixPoints;
    this.observedAt = source && source.observedAt || this.data.sourceObservedAt || EMPTY;
  }
  private applyConditions(): void {
    const evaluation = this.data.evaluation;
    const view = growthEvaluationView(evaluation, this.data.learningPreview);
    this.learningCondition = view && view.learningPreview;
    this.conditionTargetKey = view && view.targetKey || EMPTY;
    this.totalCondition = this.confirmedCondition(view && view.total);
    this.levelCondition = this.confirmedCondition(view && view.exact);
    this.conditionGradeKey = this.levelCondition.gradeCode ? dealPayrollGradeKey(this.levelCondition.gradeCode) : EMPTY;
  }
  private confirmedCondition(condition: GrowthConditionView): GrowthConditionView {
    const value: GrowthConditionView = condition || {status: EMPTY, value: null, required: null, satisfied: null,
      valueText: growthDecimalText(null), requiredText: growthDecimalText(null), statusKey: CONDITION_UNKNOWN};
    const known = value.status === VALID && growthFinitePoints(value.value) && growthFinitePoints(value.required);
    return Object.assign({}, value, {valueText: growthDecimalText(known ? value.value : null),
      requiredText: growthDecimalText(growthFinitePoints(value.required) ? value.required : null),
      statusKey: value.applicability === NOT_APPLICABLE ? CONDITION_NOT_APPLICABLE
        : known ? value.statusKey : CONDITION_UNKNOWN});
  }
  private applyLearning(): void {
    const learning = this.data.learningSummary;
    this.learning = learning && learning.userId === this.userId && this.data.calculationContext
      && learning.contextVersion === this.data.calculationContext.contextId && Array.isArray(learning.items) ? learning : null;
  }
  private fail(userId: string, generation: number, status?: number): void {
    if (!this.currentRequest(userId, generation)) { return; }
    this.loading = false; this.error = true;
    this.canRetryRead = status !== FORBIDDEN && status !== UNAUTHORIZED;
    this.errorKey = this.data ? GRADE_GROWTH_REFRESH_ERROR : GRADE_GROWTH_ERROR;
    if (this.canRetryRead) { return; }
    this.errorKey = status === FORBIDDEN ? GRADE_GROWTH_DENIED : GRADE_GROWTH_SESSION_EXPIRED;
    this.disposeRequest(); this.clearData();
  }
  private clearData(): void {
    this.sourceRequestId = null; this.sourceFeedbackKey = EMPTY; this.sourceError = false;
    this.data = null; this.learning = null; this.learningItems = []; this.milestones = [];
    this.balanceText = EMPTY; this.gradeKey = EMPTY; this.nextGradeKey = EMPTY; this.remainingText = EMPTY;
    this.moneyMonth = EMPTY; this.hasMoney = false;
    this.referenceMonth = EMPTY; this.hasReference = false;
    this.learningExpanded = false; this.learningLimit = LEARNING_PAGE_SIZE; this.balance = null;
    this.learningErrorKey = EMPTY; this.learningNeedsRefresh = false;
    this.layout = growthChartLayout([], null, {width: INITIAL_CHART_WIDTH}); this.layoutDirty = true;
  }
  private currentRequest(userId: string, generation: number): boolean {
    return userId === this.userId && generation === this.generation && this.owner === this.context.owner();
  }
  toggleLearning(): void {
    this.learningExpanded = !this.learningExpanded; this.learningLimit = LEARNING_PAGE_SIZE; this.updateLearningItems();
    if (!this.learningExpanded && this.learningToggle) { this.learningToggle.nativeElement.focus(); }
  }
  moreLearning(): void {
    if (!this.learning || !this.learningExpanded || this.learningLoading || this.loading || this.learningNeedsRefresh) { return; }
    if (this.learning.items.length > this.learningLimit) {
      this.rememberLearningFocus();
      this.learningLimit += LEARNING_PAGE_SIZE; this.updateLearningItems(); return;
    }
    if (!this.learning.nextCursor) { return; }
    this.disposeLearningRequest();
    this.rememberLearningFocus();
    const request: LearningPageRequest = {userId: this.userId, contextVersion: this.learning.contextVersion,
      cursor: this.learning.nextCursor, generation: this.generation, sequence: this.learningSequence};
    this.learningLoading = true; this.learningErrorKey = EMPTY;
    this.learningRequest = this.http.get<GrowthLearningSummary>(this.context.userUrl(request.userId)
      + GROWTH_PATH + LEARNING_PAGE_PATH + encodeURIComponent(request.cursor))
      .subscribe(value => this.applyLearningPage(request, value), error => this.failLearningPage(request, error && error.status));
  }
  private applyLearningPage(request: LearningPageRequest, value: GrowthLearningSummary): void {
    if (!this.currentLearningRequest(request)) { return; }
    this.learningLoading = false;
    if (!value || value.userId !== request.userId || !Array.isArray(value.items)
        || value.items.length > LEARNING_PAGE_SIZE || value.nextCursor === request.cursor) {
      this.learningErrorKey = LEARNING_PAGE_ERROR; return;
    }
    if (value.contextVersion !== request.contextVersion) {
      this.learningErrorKey = LEARNING_CONTEXT_CHANGED; this.learningNeedsRefresh = true; return;
    }
    const added = value.items.filter(item => item && item.courseId
      && !this.learning.items.some(existing => existing.courseId === item.courseId));
    this.learning = Object.assign({}, this.learning, {items: this.learning.items.concat(added), nextCursor: value.nextCursor});
    this.data.learningSummary = this.learning;
    this.learningLimit += LEARNING_PAGE_SIZE; this.updateLearningItems();
  }
  private failLearningPage(request: LearningPageRequest, status?: number): void {
    if (!this.currentLearningRequest(request)) { return; }
    this.learningLoading = false;
    if (status === UNAUTHORIZED || status === FORBIDDEN) { this.fail(request.userId, request.generation, status); return; }
    this.learningErrorKey = LEARNING_PAGE_ERROR;
  }
  private currentLearningRequest(request: LearningPageRequest): boolean {
    return this.currentRequest(request.userId, request.generation)
      && request.sequence === this.learningSequence && !!this.learning && request.contextVersion === this.learning.contextVersion;
  }
  private disposeLearningRequest(): void {
    this.learningActionFocus = null;
    this.learningSequence++; this.learningLoading = false;
    if (this.learningRequest) { this.learningRequest.unsubscribe(); this.learningRequest = null; }
  }
  private updateLearningItems(): void {
    this.learningItems = this.learning && this.learningExpanded ? this.learning.items.slice(ZERO, this.learningLimit) : [];
  }
  readonly trackLearningCount = trackGrowthLearningCount;
  trackGrade(_index: number, grade: GrowthMilestone): string { return grade.code; }
  trackLearning(_index: number, learning: GrowthLearningItem): string { return learning.courseId; }
  private disposeRequest(): void {
    if (this.sourceFeedbackKey === GROWTH_IMPORT_FEEDBACK.loading) { this.sourceFeedbackKey = EMPTY; }
    this.readActionFocus = null;
    this.disposeLearningRequest();
    this.generation++;
    if (this.request) { this.request.unsubscribe(); this.request = null; }
  }

  private focusedControl(control: ElementRef): HTMLElement {
    const element: HTMLElement = control && control.nativeElement;
    return element && element.ownerDocument.activeElement === element ? element : null;
  }
  private rememberLearningFocus(): void {
    this.learningActionFocus = this.focusedControl(this.learningMore) || this.focusedControl(this.learningRetry);
  }
  private restoreActionFocus(): void {
    if (!this.expanded) { this.readActionFocus = null; this.learningActionFocus = null; return; }
    if (this.loading || this.learningLoading) { return; }
    this.readActionFocus = this.restoreRemovedControl(this.readActionFocus, this.refreshButton);
    this.learningActionFocus = this.restoreRemovedControl(this.learningActionFocus, this.learningToggle);
  }
  private restoreRemovedControl(source: HTMLElement, target: ElementRef): HTMLElement {
    if (!source) { return null; }
    const owner = source.ownerDocument;
    if (owner.documentElement.contains(source)) { return null; }
    if (owner.activeElement !== owner.body && owner.activeElement !== source) { return null; }
    const element: HTMLButtonElement = target && target.nativeElement;
    if (element && !element.disabled && owner.documentElement.contains(element)) { element.focus(); }
    return null;
  }

  toggleFullscreen(): void {
    if (this.fullscreen) { this.exitFullscreen(); return; }
    const element = <FullscreenTarget>(this.root && this.root.nativeElement);
    if (!element) { return; }
    this.nativeDocument = element.ownerDocument;
    this.nativeDocument.addEventListener(FULLSCREEN_CHANGE, this.onFullscreenChange);
    this.nativeDocument.addEventListener(WEBKIT_FULLSCREEN_CHANGE, this.onFullscreenChange);
    this.restoreFocus = <HTMLElement>this.nativeDocument.activeElement;
    const request = element.requestFullscreen || element.webkitRequestFullscreen || element.msRequestFullscreen;
    if (!request) { return; }
    try {
      const result = request.call(element);
      if (result && (<Promise<void>>result).catch) { (<Promise<void>>result).catch(() => this.restoreFocus = null); }
    } catch (_error) { this.restoreFocus = null; }
  }
  private onFullscreenChange = (): void => {
    if (!this.nativeDocument) { return; }
    this.fullscreen = this.nativeDocument.fullscreenElement === (this.root && this.root.nativeElement)
      || this.nativeDocument.webkitFullscreenElement === (this.root && this.root.nativeElement);
    this.layoutDirty = true;
    if (!this.fullscreen && this.restoreFocus) { this.restoreFocus.focus(); this.restoreFocus = null; }
  }
  private exitFullscreen(): void {
    if (!this.nativeDocument) { return; }
    const active = this.nativeDocument.fullscreenElement || this.nativeDocument.webkitFullscreenElement;
    const exit = this.nativeDocument.exitFullscreen || this.nativeDocument.webkitExitFullscreen || this.nativeDocument.msExitFullscreen;
    if (!active || !this.root || active !== this.root.nativeElement || !exit) { return; }
    try {
      const result = exit.call(this.nativeDocument);
      if (result && (<Promise<void>>result).catch) { (<Promise<void>>result).catch(() => {}); }
    } catch (_error) { /* Browser rejected teardown. */ }
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/grade-growth/grade-growth.component.ts